import config from "@/lib/config";

export const SITE_NAME = "Arsoc";
export const SITE_TAGLINE = "Software Built for African Business";
export const DEFAULT_TITLE = `${SITE_NAME} — ${SITE_TAGLINE}`;
export const DEFAULT_DESCRIPTION =
  "Arsoc is a technology company based in Zimbabwe. We build custom software platforms — including Alpha and Estman — and provide information security services and hardware infrastructure including networking and CCTV installations.";
export const DEFAULT_OG_IMAGE = "/arsoc-icon.png";

export function getSiteUrl(): string {
  return config.siteUrl;
}

export function absoluteUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === "/") {
    return getSiteUrl();
  }
  return `${getSiteUrl()}${normalized}`;
}

export type PageSeo = {
  path: string;
  title?: string;
  description: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function webPageJsonLd(name: string, description: string, path: string) {
  const siteUrl = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: absoluteUrl(path),
    isPartOf: { "@id": `${siteUrl}/#website` },
    about: { "@id": `${siteUrl}/#organization` },
  };
}

function softwareJsonLd(name: string, description: string, path: string, category: string) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url: absoluteUrl(path),
    applicationCategory: category,
    operatingSystem: "Web",
    provider: { "@id": `${getSiteUrl()}/#organization` },
  };
}

export const pageSeo = {
  home: {
    path: "/",
    description: DEFAULT_DESCRIPTION,
    jsonLd: webPageJsonLd(DEFAULT_TITLE, DEFAULT_DESCRIPTION, "/"),
  },
  about: {
    path: "/about",
    title: "About",
    description:
      "Learn about Arsoc, a Rothkos Group company based in Zimbabwe building custom business platforms, information security services, and networking & CCTV hardware for African organisations.",
    jsonLd: webPageJsonLd(
      "About — Arsoc",
      "Learn about Arsoc, a Rothkos Group company based in Zimbabwe building custom business platforms, information security services, and networking & CCTV hardware for African organisations.",
      "/about",
    ),
  },
  contact: {
    path: "/contact",
    title: "Contact",
    description:
      "Get in touch with Arsoc about a software project, information security services, networking & CCTV installations, or partnership opportunities. Based in Harare, Zimbabwe.",
    jsonLd: webPageJsonLd(
      "Contact — Arsoc",
      "Get in touch with Arsoc about a software project, information security services, networking & CCTV installations, or partnership opportunities. Based in Harare, Zimbabwe.",
      "/contact",
    ),
  },
  alpha: {
    path: "/products/alpha",
    title: "Alpha",
    description:
      "Alpha is Arsoc’s business management platform for distributed organisations — stock, customers, agent networks, and WhatsApp-integrated operations in one place.",
    jsonLd: [
      webPageJsonLd(
        "Alpha — Arsoc",
        "Alpha is Arsoc’s business management platform for distributed organisations — stock, customers, agent networks, and WhatsApp-integrated operations in one place.",
        "/products/alpha",
      ),
      softwareJsonLd(
        "Alpha",
        "Business management platform for distributed organisations, with stock, customer records, agent networks, and WhatsApp integration.",
        "/products/alpha",
        "BusinessApplication",
      ),
    ],
  },
  estman: {
    path: "/products/estman",
    title: "Estman",
    description:
      "Estman is Arsoc’s property management system for African markets — listings, leases, tenant communication, payments, and maintenance in one platform.",
    jsonLd: [
      webPageJsonLd(
        "Estman — Arsoc",
        "Estman is Arsoc’s property management system for African markets — listings, leases, tenant communication, payments, and maintenance in one platform.",
        "/products/estman",
      ),
      softwareJsonLd(
        "Estman",
        "Property portfolio management for landlords, agencies, and property managers — listings, leases, tenants, payments, and maintenance.",
        "/products/estman",
        "BusinessApplication",
      ),
    ],
  },
  privacy: {
    path: "/privacy-policy",
    title: "Privacy Policy",
    description:
      "Read how Arsoc collects, uses, and protects personal data across our website and software products.",
    jsonLd: webPageJsonLd(
      "Privacy Policy — Arsoc",
      "Read how Arsoc collects, uses, and protects personal data across our website and software products.",
      "/privacy-policy",
    ),
  },
  terms: {
    path: "/terms",
    title: "Terms of Service",
    description:
      "Terms of service for using the Arsoc website and software products, including Alpha and Estman.",
    jsonLd: webPageJsonLd(
      "Terms of Service — Arsoc",
      "Terms of service for using the Arsoc website and software products, including Alpha and Estman.",
      "/terms",
    ),
  },
  dataDeletion: {
    path: "/data-deletion",
    title: "Data Deletion",
    description:
      "Request deletion of personal data that Arsoc holds. This page explains how to submit a request and what to expect.",
    jsonLd: webPageJsonLd(
      "Data Deletion — Arsoc",
      "Request deletion of personal data that Arsoc holds. This page explains how to submit a request and what to expect.",
      "/data-deletion",
    ),
  },
} satisfies Record<string, PageSeo>;
